import { KeyboardEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { IUser } from "../types/entities";
import { isLocal } from "./config";

function Truthy<T>(
  value: T
): value is Exclude<T, false | null | undefined | "" | 0> {
  return Boolean(value);
}
export function isTruthy<T>(value: T): boolean {
  if (typeof value === "function") return Boolean(value());
  if (value instanceof Array) return Boolean(value.length);
  if (value instanceof Object) return Boolean(Object.keys(value).length);
  return Boolean(value);
}

export const getUser = (): {
  accountName: string;
  admin: boolean;
  email: string;
  id: number;
  onboardingComplete: boolean;
} | null => {
  const { isF1 } = getAppType();
  const user = JSON.parse(
    localStorage.getItem(isF1 ? "ae_user_f1" : "ae_user") || "{}"
  ) as IUser;

  return {
    accountName: user?.accountName || "",
    admin: user?.admin || false,
    email: user?.email || "",
    id: user?.id || 0,
    onboardingComplete: user?.onboardingComplete || false
  };
};

const removeCharFromStringAndJoin = (
  str: string,
  charToRemove: string
): string => {
  const strArray = str?.split(charToRemove);
  return strArray?.join(" ");
};

const getTrimmedString = (str: string): string =>
  str && typeof str === "string" ? str?.trim() : "";

export { Truthy, removeCharFromStringAndJoin, getTrimmedString };

export const onEnterKeyPressed = <T>(
  event: KeyboardEvent<T>,
  callbackFn: () => void
): void => {
  if (event.key === "Enter") callbackFn();
};

export const generateUniqueId = (): string => uuidv4();

/** "dev@aptedge.io" -> "Dev". whoami has no display name, so the local part is the best we have. */
export const firstNameFromEmail = (email?: string): string => {
  const local = email?.split("@")[0].split(/[._-]/)[0] ?? "";
  return local ? local[0].toUpperCase() + local.slice(1) : "";
};

/** "dev@aptedge.io" -> "DV", "jane.smith@x" -> "JS". */
export const initialsFromEmail = (email?: string): string => {
  const parts = email?.split("@")[0].split(/[._-]/).filter(Boolean) ?? [];
  const src = parts.length > 1 ? parts.map((p) => p[0]) : [...(parts[0] ?? "")];
  return src.slice(0, 2).join("").toUpperCase();
};

export const getLocalGreeting = (): string => {
  const hour = new Date().getHours();

  if (hour >= 17 || hour < 4) {
    return "Good evening";
  }

  if (hour >= 4 && hour < 12) {
    return "Good morning";
  }

  return "Good afternoon";
};

const APP_TYPES = {
  f1: "f1",
  webApp: "web",
  zendeskApp: "zendesk",
  salesForceApp: "salesforce",
  serviceNowApp: "servicenow"
} as const;

// The type this bundle set. getAppType prefers it to APP_TYPE in localStorage,
// which every app on the origin shares: the self-service iframe embedded in the
// web app writes "f1" there. Support apps still read localStorage on their
// first render, before their setAppType effect runs.
let declaredAppType = "";

export const setAppType = (
  selector: (apps: typeof APP_TYPES) => string
): void => {
  if (declaredAppType) return;
  declaredAppType = selector(APP_TYPES);

  localStorage.setItem("APP_TYPE", declaredAppType);
};

let cachedAppType: ReturnType<typeof getAppType> | null = null;

type GetAppType = {
  type: string;
  isF1: boolean;
  isWebApp: boolean;
  isSupportApp: boolean;
  isZendesk: boolean;
  isSalesforce: boolean;
  isServiceNow: boolean;
};

export const getAppType = (): GetAppType => {
  if (cachedAppType) return cachedAppType;

  const appType =
    declaredAppType ||
    ((isLocal() && window.location.href.includes("localhost:3002")) ||
    (!isLocal() && window.location.href.includes("/self-service/"))
      ? APP_TYPES.f1
      : localStorage.getItem("APP_TYPE") || "");

  cachedAppType = {
    type: appType,
    isF1: appType === APP_TYPES.f1,
    isWebApp: appType === APP_TYPES.webApp,
    isZendesk: appType === APP_TYPES.zendeskApp,
    isSalesforce: appType === APP_TYPES.salesForceApp,
    isServiceNow: appType === APP_TYPES.serviceNowApp,
    isSupportApp:
      appType === APP_TYPES.salesForceApp ||
      appType === APP_TYPES.serviceNowApp ||
      appType === APP_TYPES.zendeskApp
  };

  return cachedAppType;
};
